import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import type { CostsFiltersPayload, CostsViewMode, IPaginationPayload } from '../../../types/costs';
import ViewModeTabs from './components/ViewModeTabs';
import CostsFiltersBar from './components/CostsFiltersBar';
import StatsCards from './components/StatsCards';
import CostsCharts from './components/CostsCharts';
import ClientCostsTable from './components/ClientCostsTable';
import { tryToFetchLeoCosts, tryToFetchLeoStats } from '../../../store/costs/costsSlice';
import { useGetAIModelsQuery } from '../../../store/chatbox/chatboxApi';
import { useNavigate } from 'react-router-dom';

const ClientCostsPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const statsLoading = useAppSelector((s) => s.clientCosts.loadingStats);
    const data = useAppSelector((s) => s.clientCosts.data);
    const dataStats = useAppSelector((s) => s.clientCosts.stats);
    const { data: models } = useGetAIModelsQuery();

    const [viewMode, setViewMode] = useState<CostsViewMode>('mix');
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({ pageNumber: 1, pageSize: 10 });

    const [filters, setFilters] = useState<CostsFiltersPayload>({
        dateType: undefined,
        provider: undefined,
    });

    const [accumulatedRows, setAccumulatedRows] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState(true);

    const [isInitialTableLoading, setIsInitialTableLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loadingMoreRef = useRef(false);
    const currentPageRef = useRef(1);
    const costsRequestVersionRef = useRef(0);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const stats = dataStats?.data;

    const platforms = (models?.data || [])
        .map((model: any) => {
            const id = model?.id || model?._id;
            if (!id) return null;
            return {
                label: (model?.name || '').toLowerCase() === 'chatgpt' ? 'ChatGPT' : model?.name,
                name: model?.name,
                value: String(id),
                icon: model?.logoUrl,
            };
        })
        .filter(Boolean);

    const mapApiItemToSummaryRow = (item: any) => {
        const providers = item.breakdown?.byProvider?.map((p: any) => p.provider) ?? [];
        return {
            clientId: item?.client?.userId,
            id: item.companySub,
            clientName: item.client?.name,
            companyName: item.client?.companyName,
            username: item.client?.username,
            email: item.client?.email,
            totalUsd: item.totals?.totalUsd ?? 0,
            calls: item.totals?.calls ?? 0,
            requests: item.totals?.requests ?? 0,
            inputTokens: item.totals?.inputTokens ?? 0,
            outputTokens: item.totals?.outputTokens ?? 0,
            providers: providers?.length,
        };
    };

    const totalCount = data?.page?.totalElements;

    const buildCostsPayload = (pageNumber: number) => {
        return {
            pageNumber,
            pageSize: paginationState.pageSize,
            data: {
                ...filters,
            },
            filters: filters?.search,
        };
    };

    const fetchCosts = async (
        mode: 'initial' | 'loadMore',
        pageNumber: number,
        requestVersion: number,
    ) => {
        try {
            if (mode === 'loadMore') {
                setIsLoadingMore(true);
            } else {
                setIsInitialTableLoading(true);
            }

            const payload = buildCostsPayload(pageNumber);

            const result = await dispatch(tryToFetchLeoCosts(payload)).unwrap();

            if (requestVersion !== costsRequestVersionRef.current) return;

            const newElements = result?.data?.elements ?? [];
            const newPage = result?.data?.page;
            const nextHasMore = newPage?.hasNextPage ?? false;

            if (mode === 'loadMore') {
                currentPageRef.current = pageNumber;
                setPaginationState((prev) => ({ ...prev, pageNumber }));
                setAccumulatedRows((prev) => [...prev, ...newElements]);
            } else {
                setAccumulatedRows(newElements);
            }
            setHasMore(nextHasMore);
        } catch (err) {
            console.error(err);
        } finally {
            if (requestVersion === costsRequestVersionRef.current) {
                if (mode === 'loadMore') {
                    setIsLoadingMore(false);
                    loadingMoreRef.current = false;
                } else {
                    setIsInitialTableLoading(false);
                    loadingMoreRef.current = false;
                }
            }
        }
    };

    const fetchStats = async () => {
        try {
            await dispatch(tryToFetchLeoStats(filters)).unwrap();
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        const requestVersion = costsRequestVersionRef.current + 1;
        costsRequestVersionRef.current = requestVersion;
        currentPageRef.current = 1;
        loadingMoreRef.current = true;

        setPaginationState((prev) => ({ ...prev, pageNumber: 1 }));
        setAccumulatedRows([]);
        setHasMore(true);
        setIsLoadingMore(false);

        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }

        fetchCosts('initial', 1, requestVersion);
        fetchStats();
    }, [filters]);

    const onApplyFilters = () => {
        loadingMoreRef.current = true;
        costsRequestVersionRef.current += 1;
        setFilters((prev) => ({ ...prev }));
    };

    const onChangeFilters = (nextFilters: CostsFiltersPayload) => {
        loadingMoreRef.current = true;
        costsRequestVersionRef.current += 1;
        setFilters(nextFilters);
    };

    const onLoadMore = () => {
        if (loadingMoreRef.current || isLoadingMore || isInitialTableLoading || !hasMore) return;

        loadingMoreRef.current = true;
        fetchCosts('loadMore', currentPageRef.current + 1, costsRequestVersionRef.current);
    };

    const showStats = viewMode === 'mix' || viewMode === 'stats';
    const showTable = viewMode === 'mix' || viewMode === 'table';

    const onClickViewDetails = (clientId: string) => {
        navigate(`/costs/${clientId}`);
    };

    const rows = useMemo(() => {
        return (accumulatedRows || []).map((item: any) => mapApiItemToSummaryRow(item));
    }, [accumulatedRows]);

    return (
        <div className='w-full'>
            <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
                    <div className='flex flex-col'>
                        <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                            Leo LLM Costs
                        </h1>
                        <p className='text-sm text-gray-600 dark:text-gray-300'>
                            Track usage costs across clients, properties and scenarios.
                        </p>
                    </div>

                    <div className='flex items-center justify-start md:justify-end'>
                        <ViewModeTabs value={viewMode} onChange={setViewMode} />
                    </div>
                </div>

                <div className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black'>
                    <CostsFiltersBar
                        platforms={platforms}
                        value={filters}
                        onChange={onChangeFilters}
                        onApply={onApplyFilters}
                        loading={statsLoading}
                    />
                </div>

                {showStats && (
                    <div className='flex flex-col gap-4'>
                        <StatsCards loading={statsLoading} backendResponse={stats} />
                        <div className='grid grid-cols-1 lg:grid-cols-12 gap-4'>
                            <div className='lg:col-span-12'>
                                <div className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-3 md:p-4'>
                                    <CostsCharts loading={statsLoading} backendResponse={stats} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showTable && (
                    <div className='rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-0'>
                        <div className='px-4 pt-4 pb-2 flex flex-col gap-1 md:flex-row md:items-center md:justify-between'>
                            <div className='flex flex-col'>
                                <p className='text-base font-semibold text-[#101010] dark:text-primary-darkMode'>
                                    Clients
                                </p>
                                <p className='text-xs text-gray-600 dark:text-gray-300'>
                                    Showing costs for the selected filters.
                                </p>
                            </div>

                            {totalCount != null && (
                                <div className='text-xs text-gray-500 dark:text-gray-400'>
                                    {totalCount} total
                                </div>
                            )}
                        </div>

                        <div className='px-4 pb-4'>
                            <ClientCostsTable
                                rows={rows}
                                loading={isInitialTableLoading}
                                hasMore={hasMore}
                                loadingMore={isLoadingMore}
                                onLoadMore={onLoadMore}
                                scrollContainerRef={scrollContainerRef}
                                onViewDetails={onClickViewDetails}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientCostsPage;
